import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';
import { useTranslation } from 'react-i18next';

import ModuleFormService, {
  ModuleFormServiceSuccess,
} from '@/components/module/ModuleFormService';
import PedContentTabContent from '@/components/ped-content/PedContentTabContent';
import WorkspaceLayout from '@/components/shared/WorkspaceLayout';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Skeleton, Tabs } from '@/components/ui/daisy';
import { useTreeCourses } from '@/data/context/TreeCoursesContext';
import { useInfiniteCache } from '@/data/hook/query/v2/useCacheManager';
import useModuleWorkspace from '@/data/hook/module/useModuleWorkspace';
import useWorkspaceTabs from '@/data/hook/useWorkspaceTabs';
import { courseModulesQueryKeys } from '@/data/services/querykeys';
import { mountKey } from '@/utils/courses';
import { ROUTES } from '@/utils/routes';

export enum ModuleTabs {
  INFO = 'info',
  PED_CONTENT = 'ped_content',
}

export default function ModuleWorkspace() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.moduleForm.workspace',
  });

  const { tree } = useTreeCourses();
  const { module, courseLineId, moduleId, isLoading } = useModuleWorkspace();
  const { tab, setTab } = useWorkspaceTabs<ModuleTabs>({
    defaultTab: ModuleTabs.INFO,
  });
  const cacheManager = useInfiniteCache(
    courseModulesQueryKeys.list(courseLineId)._ctx.infinity.queryKey,
  );
  const { navigate } = useCourseTreeNavigate();

  const handleSuccessUpdate = (response: ModuleFormServiceSuccess) => {
    cacheManager.update(response.item);
    tree?.current.update({
      id: mountKey({ type: 'module', id: response.item.id }),
      name: response.item.name,
    });
  };

  const handleSuccessCreate = (response: ModuleFormServiceSuccess) => {
    cacheManager.add(response.item, { position: 'end' });
    tree?.current.upsert(
      {
        id: mountKey({ type: 'module', id: response.item.id }),
        name: response.item.name,
        type: 'module',
        realId: response.item.id,
      },
      mountKey({ type: 'courseLine', id: courseLineId }),
    );
    navigate(ROUTES.ADMIN.COURSE_LINES.MODULE(courseLineId, response.item.id), {
      treeKey: mountKey({ type: 'module', id: response.item.id }),
    });
  };

  const handleSuccess = (response: ModuleFormServiceSuccess) => {
    if (response.action === 'update') handleSuccessUpdate(response);
    else handleSuccessCreate(response);
  };

  if (isLoading) return <LoadingView />;

  return (
    <WorkspaceLayout
      title={t('title')}
      subtitle={module?.name || t('newModule')}
    >
      <Tabs
        variant="box"
        className="w-fit"
        value={tab}
        onChange={value => setTab(value as ModuleTabs)}
      >
        <Tabs.Tab value={ModuleTabs.INFO}>{t('tabs.information')}</Tabs.Tab>
        <Tabs.Tab value={ModuleTabs.PED_CONTENT} disabled={!module}>
          {t('tabs.contents')}
        </Tabs.Tab>
      </Tabs>
      <ConditionalRenderer condition={tab === ModuleTabs.INFO}>
        <ModuleFormService key={module?.id} onSuccess={handleSuccess} />
      </ConditionalRenderer>
      <ConditionalRenderer
        condition={tab === ModuleTabs.PED_CONTENT && !!module}
      >
        {module && (
          <PedContentTabContent
            moduleId={moduleId}
            courseLineId={courseLineId}
          />
        )}
      </ConditionalRenderer>
    </WorkspaceLayout>
  );
}

const LoadingView = () => {
  return (
    <div className="flex flex-col flex-1 rounded-2xl p-6 gap-6 bg-base-100">
      <div className="flex flex-col gap-5">
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-8 w-40" />
      </div>
      <Skeleton className="h-12 w-52" />
      <Skeleton className="h-96 w-full" />
      <div className="flex justify-end">
        <Skeleton className="h-12 w-32" />
      </div>
    </div>
  );
};
